import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import useTheme from '../hooks/useTheme';
import useFeatureFlags from '../hooks/useFeatureFlags';
import CurrencySwitcher from './CurrencySwitcher';
import './Header.css';

const Header = () => {
  const flags = useFeatureFlags();
  const { getCartItemsCount, openDrawer } = useCart();
  const { theme, toggle: toggleTheme } = useTheme();
  const location = useLocation(); // syncs with router; window.location.pathname misses client navigations
  const [categories, setCategories] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  // Which dropdown is open on touch / click. CSS hover still handles the
  // mouse case; this state drives the same `.dropdown.active` selector for
  // touch devices that never fire :hover.
  const [openDropdown, setOpenDropdown] = useState(null); // 'categories' | 'more' | null

  // Close any open dropdown on outside click + on Escape.
  useEffect(() => {
    if (!openDropdown) return;
    const onDocClick = (e) => {
      if (!e.target.closest?.('.dropdown')) setOpenDropdown(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpenDropdown(null); };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [openDropdown]);

  // Close the open dropdown whenever the route changes.
  useEffect(() => { setOpenDropdown(null); }, [location.pathname]);

  const toggleDropdown = (key) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenDropdown(prev => (prev === key ? null : key));
  };

  useEffect(() => {
    fetch('/api/categories')
      .then(res => res.json())
      .then(data => setCategories(data))
      .catch(err => console.error('Error fetching categories:', err));

    // Check if user is logged in. Defensive parse: corrupted localStorage
    // shouldn't crash the header on every page load.
    const readUser = () => {
      try {
        const userData = localStorage.getItem('user');
        setUser(userData ? JSON.parse(userData) : null);
      } catch (_) {
        try { localStorage.removeItem('user'); } catch {}
        setUser(null);
      }
    };
    readUser();

    // Bug fix: previously this only ran on mount, so when Login navigated
    // via react-router (no full reload) the header kept saying "Sign In"
    // even though localStorage already had the user. Now we also re-read
    // on cross-tab storage events AND on a same-tab 'auth-change' event
    // that Login/Register/Logout dispatch.
    const onStorage = (e) => {
      if (e.key === 'user' || e.key === 'token' || e.key === null) readUser();
    };
    const onAuthChange = () => readUser();
    window.addEventListener('storage', onStorage);
    window.addEventListener('auth-change', onAuthChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth-change', onAuthChange);
    };
  }, []);

  const cartCount = getCartItemsCount();

  return (
    <header className="header" role="banner">
      <div className="header-top">
        <div className="container">
          <Link to="/" className="logo" aria-label="SilverGames Home">
            <h1>SilverGames</h1>
            <p className="tagline">Your Friendly Pokemon Card Shop</p>
          </Link>

          <div className="header-actions">
            {user ? (
              <Link
                to="/account"
                className="account-link"
                aria-label={`Account for ${user.first_name}`}
              >
                <span className="account-name">{user.first_name}</span>
              </Link>
            ) : (
              <Link to="/login" className="login-link" aria-label="Sign in to your account">
                Sign In
              </Link>
            )}

            <CurrencySwitcher />

            <button
              type="button"
              onClick={toggleTheme}
              className="theme-toggle"
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? 'Light' : 'Dark'}
            </button>

            <button
              type="button"
              onClick={openDrawer}
              className="cart-link"
              aria-label={`Shopping cart with ${cartCount} item${cartCount !== 1 ? 's' : ''}`}
            >
              {/* Inline SVG cart icon — sharp at any DPI, themable via
                  currentColor, no emoji rendering inconsistencies. */}
              <svg
                className="cart-icon"
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="20" r="1.4" />
                <circle cx="18" cy="20" r="1.4" />
                <path d="M2 3h3l2.4 12.2a2 2 0 0 0 2 1.6h8.6a2 2 0 0 0 2-1.6L22 7H6" />
              </svg>
              {cartCount > 0 && (
                <span className="cart-count" aria-label={`${cartCount} items in cart`}>
                  {cartCount}
                </span>
              )}
            </button>

            {/* Admin entry intentionally not advertised — staff bookmark /admin/login or
                use the keyboard shortcut: type 'g' then 'a' on any page. Reduces public
                reconnaissance surface for credential-stuffing attempts. */}
          </div>
        </div>
      </div>

      <nav className="nav" aria-label="Main navigation">
        <div className="container">
          <button
            className="mobile-menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="main-menu"
          >
            <span aria-hidden="true">{isMenuOpen ? '✕' : '☰'}</span>
          </button>

          <ul id="main-menu" className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
            <li>
              <Link
                to="/"
                onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }}
                aria-current={location.pathname === '/' ? 'page' : undefined}
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                to="/shop"
                onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }}
                aria-current={location.pathname === '/shop' ? 'page' : undefined}
              >
                All Products
              </Link>
            </li>
            {flags.ai_grading !== false && (
              <li>
                <Link
                  to="/grade"
                  onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }}
                  aria-current={location.pathname.startsWith('/grade') ? 'page' : undefined}
                  style={{ fontWeight: 600 }}
                >
                  Grade a Card
                </Link>
              </li>
            )}
            <li className={`dropdown dropdown--mega ${openDropdown === 'categories' ? 'active' : ''}`}>
              <span
                className="dropdown-toggle" role="button" tabIndex="0"
                aria-haspopup="true" aria-expanded={openDropdown === 'categories'}
                onClick={toggleDropdown('categories')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleDropdown('categories')(e); }}
              >
                Categories <span aria-hidden="true">▼</span>
              </span>
              <CategoriesMegaMenu
                categories={categories}
                onNavigate={() => { setIsMenuOpen(false); setOpenDropdown(null); }}
              />
            </li>
            <li className={`dropdown ${openDropdown === 'more' ? 'active' : ''}`}>
              <span
                className="dropdown-toggle" role="button" tabIndex="0"
                aria-haspopup="true" aria-expanded={openDropdown === 'more'}
                onClick={toggleDropdown('more')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleDropdown('more')(e); }}
              >
                More <span aria-hidden="true">▼</span>
              </span>
              <ul className="dropdown-menu" role="menu">
                {flags.mass_entry_enabled !== false && (
                  <li role="none"><Link to="/mass-entry" onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }} role="menuitem">Paste a list</Link></li>
                )}
                <li role="none"><Link to="/sell"        onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }} role="menuitem">Sell to us</Link></li>
                <li role="none"><Link to="/track"       onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }} role="menuitem">Track order</Link></li>
                {flags.auctions && (
                  <li role="none"><Link to="/auctions"  onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }} role="menuitem">Auctions</Link></li>
                )}
                {flags.pre_orders !== false && (
                  <li role="none"><Link to="/pre-orders"  onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }} role="menuitem">Pre-orders</Link></li>
                )}
                <li role="none"><Link to="/events"      onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }} role="menuitem">Events</Link></li>
                <li role="none"><Link to="/blog"        onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }} role="menuitem">Blog</Link></li>
                <li role="none"><Link to="/decks"       onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }} role="menuitem">My Decks</Link></li>
                <li role="none"><Link to="/collection"  onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }} role="menuitem">My Collection</Link></li>
                {flags.trade_ins !== false && (
                  <li role="none"><Link to="/trade-ins"   onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }} role="menuitem">Trade-ins</Link></li>
                )}
              </ul>
            </li>
            <li>
              <Link
                to="/coming-soon"
                onClick={() => { setIsMenuOpen(false); setOpenDropdown(null); }}
                className="coming-soon-link"
                aria-label="Coming soon - More card games"
              >
                Coming Soon
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

// Mega-menu for the Categories dropdown.
//   ┌─────────────────────────────────────────────────────────┐
//   │ [● Pokémon] [ MTG ] [ Yu-Gi-Oh! ] [ One Piece ] [ … ]   │ ← game tabs
//   ├─────────────────────────────────────────────────────────┤
//   │ Browse by Era │ Sealed │ Singles & Graded │ Accessories │
//   └─────────────────────────────────────────────────────────┘
// Defaults to Pokémon. Coming-soon games show an inert tab + an empty-state
// panel. Game tabs are derived from `kind='game'` rows in /api/categories.
function CategoriesMegaMenu({ categories, onNavigate }) {
  const byKind = (k) => categories
    .filter(c => (c.kind || 'set') === k && !c.is_hidden)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.name || '').localeCompare(b.name || ''));

  const games = byKind('game');

  // Build a parent → children map once so we can scope eras/series/sets/etc.
  // under the active game tab without re-walking the tree per render.
  const childrenOf = React.useMemo(() => {
    const m = new Map();
    for (const c of categories) {
      if (c.parent_id == null) continue;
      if (!m.has(c.parent_id)) m.set(c.parent_id, []);
      m.get(c.parent_id).push(c);
    }
    return m;
  }, [categories]);

  // Descendants of a game (era → series → set, plus product/accessory types
  // that may be cross-game-attached down the road). Defaults to a flat fallback
  // for games with zero hierarchy yet.
  const descendantsOf = (gameId) => {
    const out = [];
    const stack = [gameId];
    const seen = new Set();
    while (stack.length) {
      const cur = stack.pop();
      if (seen.has(cur)) continue;
      seen.add(cur);
      for (const ch of (childrenOf.get(cur) || [])) {
        out.push(ch);
        stack.push(ch.id);
      }
    }
    return out;
  };

  const activeOf = (game) => {
    const desc = descendantsOf(game.id);
    return desc.length > 0;
  };

  // Default to the first active game (typically Pokémon). Fall back to first
  // game in list if none are active yet.
  const defaultGameSlug = (games.find(activeOf) || games[0])?.slug || null;
  const [activeSlug, setActiveSlug] = React.useState(defaultGameSlug);
  React.useEffect(() => {
    if (defaultGameSlug && !activeSlug) setActiveSlug(defaultGameSlug);
  }, [defaultGameSlug, activeSlug]);

  // Mobile-only: which columns are expanded. Hooks must be declared before
  // any early returns so React's hook order stays stable across renders.
  const [expandedCols, setExpandedCols] = React.useState(new Set());
  const toggleCol = (h) => setExpandedCols(prev => {
    const next = new Set(prev);
    if (next.has(h)) next.delete(h); else next.add(h);
    return next;
  });

  // Legacy fallback — no games seeded yet. Render the original flat list so
  // the menu doesn't go blank during/right after deploy.
  if (!games.length) {
    return (
      <ul className="dropdown-menu" role="menu">
        {categories.slice(0, 20).map(cat => (
          <li key={cat.id} role="none">
            <Link to={`/shop/${cat.slug}`} onClick={onNavigate} role="menuitem">
              {cat.name} {cat.product_count > 0 && <span className="category-count">({cat.product_count})</span>}
            </Link>
          </li>
        ))}
      </ul>
    );
  }

  const activeGame = games.find(g => g.slug === activeSlug) || games[0];
  const activeIsActive = activeGame ? activeOf(activeGame) : false;

  // Build the four columns from the active game's descendants.
  const desc = activeGame ? descendantsOf(activeGame.id) : [];
  const eras          = desc.filter(c => c.kind === 'era').sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const productTypes  = desc.filter(c => c.kind === 'product_type');
  const accessoryTypes = desc.filter(c => c.kind === 'accessory_type');
  // Product/accessory types may not be parented under games yet — fall back to
  // the global pool so Pokémon still shows Sealed/Singles/Accessories columns.
  const allProductTypes   = productTypes.length ? productTypes : byKind('product_type');
  const allAccessoryTypes = accessoryTypes.length ? accessoryTypes : byKind('accessory_type');
  const sealed        = allProductTypes.filter(c => /booster|etb|elite|trainer|tin|collection|premium|deck|prerelease|build|pack|blister|box set|pin/i.test(c.name || '') && !/single|graded/i.test(c.name || ''));
  const singlesGraded = allProductTypes.filter(c => /single|graded|psa|bgs|cgc|sgc|tag|promo|jumbo|code/i.test(c.name || ''));
  const setsInGame    = desc.filter(c => c.kind === 'set');

  // Inline icon paths — small visual cue per column so the menu scans fast.
  const ICONS = {
    'Browse by Era':    'M3 7h18M5 7v12a2 2 0 002 2h10a2 2 0 002-2V7M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2',
    'Sealed':           'M21 8l-9-5-9 5m18 0l-9 5m9-5v8l-9 5m0-13L3 8m9 5L3 8m9 5v8',
    'Singles & Graded': 'M4 6h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2zm14 0V4a2 2 0 012-2h0a2 2 0 012 2v12',
    'Accessories':      'M12 22s8-4 8-12V5l-8-3-8 3v5c0 8 8 12 8 12z',
  };

  const column = (heading, items, linkPrefix = '/shop/') => items.length ? (
    <div className={`mega-menu__col${expandedCols.has(heading) ? ' is-expanded' : ''}`} role="group" aria-label={heading}>
      <button
        type="button"
        className="mega-menu__heading"
        onClick={() => toggleCol(heading)}
        aria-expanded={expandedCols.has(heading)}
        aria-controls={`mega-col-${heading.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <svg className="mega-menu__heading-icon" width="16" height="16" viewBox="0 0 24 24"
             fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
             aria-hidden="true">
          <path d={ICONS[heading]} />
        </svg>
        <span>{heading}</span>
        <span className="mega-menu__heading-count">{items.length}</span>
        <svg className="mega-menu__heading-chevron" width="14" height="14" viewBox="0 0 24 24"
             fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
             aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <ul id={`mega-col-${heading.replace(/\s+/g, '-').toLowerCase()}`} className="mega-menu__list">
        {items.map(c => (
          <li key={c.id}>
            <Link to={`${linkPrefix}${c.slug}`} onClick={onNavigate}>
              <span className="mega-menu__list-name">{c.name}</span>
              {c.is_new ? <span className="mega-menu__list-badge" title="New">New</span> : null}
              {c.product_count > 0 && <span className="category-count">{c.product_count}</span>}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <div className="mega-menu" role="menu">
      <div className="mega-menu__games" role="tablist" aria-label="Game">
        {games.map(g => {
          const isActiveTab = g.slug === activeGame?.slug;
          const isStocked = activeOf(g);
          // Shorter labels so all 6 game tabs fit on one line without wrapping.
          const shortLabel = ({
            'mtg':         'Magic',
            'sports-cards': 'Sports',
          })[g.slug] || g.name;
          return (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={isActiveTab}
              title={isStocked ? g.name : `${g.name} — coming soon`}
              className={`mega-menu__game${isActiveTab ? ' is-active' : ''}${isStocked ? '' : ' is-coming-soon'}`}
              onClick={() => setActiveSlug(g.slug)}
            >
              {!isStocked && <span className="mega-menu__game-dot" aria-hidden="true" />}
              <span className="mega-menu__game-label">{shortLabel}</span>
              {!isStocked && <span className="mega-menu__game-soon">Soon</span>}
            </button>
          );
        })}
      </div>

      {activeIsActive ? (
        <>
          <div className="mega-menu__columns">
            {column('Browse by Era', eras)}
            {column('Sealed', sealed)}
            {column('Singles & Graded', singlesGraded)}
            {column('Accessories', allAccessoryTypes)}
          </div>
          <div className="mega-menu__footer">
            <Link to={`/shop/${activeGame.slug}`} onClick={onNavigate} className="mega-menu__footer-cta mega-menu__footer-cta--primary">
              Shop all {activeGame.name} →
            </Link>
            {setsInGame.length > 0 && (
              <Link to={`/shop/${activeGame.slug}?kind=set`} onClick={onNavigate} className="mega-menu__footer-link">
                Browse all {setsInGame.length} sets →
              </Link>
            )}
          </div>
        </>
      ) : (
        <div className="mega-menu__empty">
          <div className="mega-menu__empty-title">{activeGame?.name} — coming soon</div>
          <p className="mega-menu__empty-body">
            We're working on stocking {activeGame?.name}. Browse Pokémon in the meantime,
            or check back soon.
          </p>
          <Link to="/shop/pokemon" onClick={onNavigate} className="mega-menu__empty-cta">
            Browse Pokémon →
          </Link>
        </div>
      )}
    </div>
  );
}

export default Header;
